import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Location } from 'src/app/models/common-models';
import { EnvironmentService } from 'src/app/services/environment.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';

declare const google: any;

/**
 * LocationInfoOverlay — robust overlay that:
 * - Attaches to floatPane (above markers)
 * - Draws only when projection is available
 * - Re-draws on idle/zoom/drag/dragend
 * - Anchors bottom-center using CSS transform
 */
class LocationInfoOverlay extends google.maps.OverlayView {
  private content: HTMLElement;
  private position: any; // google.maps.LatLng
  private onCloseCallback: () => void;
  private map: any; // google.maps.Map
  private rafId: number | null = null;
  private listeners: any[] = []; // google.maps.MapsEventListener[]

  constructor(content: HTMLElement, position: any, map: any, onClose: () => void) {
    super();
    this.content = content;
    this.position = position;
    this.map = map;
    this.onCloseCallback = onClose;
  }

  onAdd(): void {
    console.log('[Overlay] onAdd() called');
    const panes = (this as any).getPanes();
    
    // Anchor bottom-center of the target lat/lng with space above the pin
    // translate(-50%, calc(-100% - 60px)) centers horizontally and positions
    // the overlay above the marker with 60px gap (pin is ~40-50px tall)
    Object.assign(this.content.style, {
      position: 'absolute',
      transform: 'translate(-50%, calc(-100% - 60px))',
      willChange: 'transform, left, top',
      zIndex: '1001',
      pointerEvents: 'auto',
      display: 'block',
      visibility: 'visible',
      opacity: '1',
      margin: '0'
    } as CSSStyleDeclaration);
    
    // Use floatPane so the card appears above markers/tiles and remains clickable
    if (panes && panes.floatPane) {
      panes.floatPane.appendChild(this.content);
      console.log('[Overlay] Content added to floatPane');
    } else {
      console.error('[Overlay] Could not find floatPane');
      return;
    }

    // Redraw on all relevant interactions
    this.listeners.push(
      google.maps.event.addListener(this.map, 'idle', () => this.scheduleDraw()),
      google.maps.event.addListener(this.map, 'zoom_changed', () => this.scheduleDraw()),
      google.maps.event.addListener(this.map, 'drag', () => this.scheduleDraw()),
      google.maps.event.addListener(this.map, 'dragend', () => this.scheduleDraw()),
      google.maps.event.addListener(this.map, 'resize', () => this.scheduleDraw())
    );
  }

  private scheduleDraw(): void {
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
    }
    // Wait one frame for map tiles/projection to settle, then draw
    this.rafId = requestAnimationFrame(() => this.draw());
  }

  draw(): void {
    const projection = (this as any).getProjection();
    if (!projection || !this.content) {
      // Projection not ready yet - scheduleDraw will be called again by event listeners
      return;
    }
    
    const pixel = projection.fromLatLngToDivPixel(this.position);
    if (!pixel) {
      return;
    }
    
    // Access x and y properties correctly from google.maps.Point
    const posX = typeof pixel.x === 'function' ? pixel.x() : pixel.x;
    const posY = typeof pixel.y === 'function' ? pixel.y() : pixel.y;
    
    // Guard against obviously invalid numbers
    if (!isFinite(posX) || !isFinite(posY)) {
      return;
    }
    
    // Position using transform anchor (bottom-center with space above pin)
    // The transform: translate(-50%, calc(-100% - 60px)) in onAdd() will center horizontally
    // and position the overlay above the marker with 60px gap for the pin
    this.content.style.left = `${posX}px`;
    this.content.style.top = `${posY}px`;
  }

  onRemove(): void {
    // Clean up all event listeners
    this.listeners.forEach(listener => {
      google.maps.event.removeListener(listener);
    });
    this.listeners = [];
    
    // Cancel any pending draw
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    
    // Remove content from DOM
    if (this.content && this.content.parentNode) {
      this.content.parentNode.removeChild(this.content);
    }
  }

  close(): void {
    (this as any).setMap(null);
    if (this.onCloseCallback) {
      this.onCloseCallback();
    }
  }
}

/**
 * Google Maps Integration with Custom Overlay
 * - Uses CustomOverlayView instead of InfoWindow for full control
 * - Shows selected location with custom overlay
 * - TODO(INTEG): Read API key from runtime config instead of hardcoded
 */
@Component({
  selector: 'app-location-map-stub',
  templateUrl: './location-map-stub.component.html',
  styleUrls: ['./location-map-stub.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocationMapStubComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() locations: Location[] | null = [];
  @Input() selectedId: number | null = null;
  @Input() isMobile = false; // Flag to indicate mobile view
  @Input() newestLocationId: number | null = null;
  @Output() select = new EventEmitter<number | null>();

  map: any;
  markers: any[] = [];
  currentOverlay: LocationInfoOverlay | null = null;
  private currentOverlayLocationId: number | null = null; // Track which location the overlay is showing
  private readonly GOOGLE_MAPS_API_KEY = 'AIzaSyADqzKNbTLmI7DK6SHdkaKYXqL7okETZzo'; // TODO(INTEG): Get from config
  private mapInitialized = false;
  private overlayCounter = 0; // For unique IDs
  private favoritedLocations = new Set<string>(); // Track favorite state per location ID
  private resizeListener?: () => void; // Window resize listener
  mapElementId: string; // Unique ID for this map instance (public for template access)

  constructor(
    private cdr: ChangeDetectorRef,
    private environmentService: EnvironmentService,
    private localStorageService: LocalStorageService
  ) {
    // Generate unique ID for this map instance to avoid conflicts when multiple instances exist
    this.mapElementId = `locations-map-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedId'] && this.mapInitialized && this.map) {
      this.updateSelectedMarker();
    }
    if (changes['locations'] && this.mapInitialized && this.map && this.locations) {
      this.createMarkers();
    }
    // If locations are first loaded and map is not initialized yet, trigger initialization
    if (changes['locations'] && !this.mapInitialized && this.locations && this.locations.length > 0) {
      // Locations are now available, trigger map initialization if not already started
      if (!this.map) {
        this.checkAndLoadMap();
      }
    }
    // If isMobile changes, we might need to reinitialize (but with *ngIf, component will be destroyed/recreated)
    // Don't close overlay when other inputs change - keep it stable
  }

  get selected(): Location | null {
    return (this.locations || []).find(l => l.Id === this.selectedId) || null;
  }

  get selectedLocation(): Location | null {
    return this.selected;
  }

  closeMobileModal(): void {
    // Clear selection to hide modal
    this.select.emit(null);
  }

  onModalClick(): void {
    if (this.selectedLocation) {
      // Store location as temp location in localStorage before navigating
      this.localStorageService.setTempLocation(this.selectedLocation);
      // Navigate to location select page
      const locationSelectUrl = this.environmentService.getFacilityConfig().locationSelectUrl || '/get-started';
      window.location.href = locationSelectUrl;
    }
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.style.display = 'none';
      const fallback = img.nextElementSibling as HTMLElement;
      if (fallback) {
        fallback.style.display = 'block';
      }
    }
  }

  /**
   * Convert full state name to abbreviation
   * Returns the abbreviation if found, otherwise returns the original state name
   */
  getStateAbbreviation(state: string): string {
    const stateMap: { [key: string]: string } = {
      'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
      'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
      'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI', 'Idaho': 'ID',
      'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
      'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
      'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
      'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
      'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
      'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
      'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
      'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT',
      'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV',
      'Wisconsin': 'WI', 'Wyoming': 'WY',
      'District of Columbia': 'DC', 'Puerto Rico': 'PR'
    };
    
    // Check if already an abbreviation (2 letters, uppercase)
    if (state.length === 2 && state === state.toUpperCase()) {
      return state;
    }
    
    return stateMap[state] || state;
  }

  /**
   * Get first image URL from LocationImages array
   */
  getFirstImage(location: Location | null): string {
    if (!location || !location.LocationImages || location.LocationImages.length === 0) {
      return '';
    }
    return location.LocationImages[0].ImageUrl || '';
  }

  /**
   * Format distance for display
   * Returns formatted string like "2.3 miles away" or null if no distance
   */
  getDistanceDisplay(location: Location): string | null {
    const distance = (location as any).distance;
    if (distance === undefined || distance === null) {
      return null;
    }
    return `${distance.toFixed(1)} miles away`;
  }

  /**
   * Open Google Maps with directions to the location
   */
  getDirections(location: Location, event?: Event): void {
    if (event) {
      event.stopPropagation(); // Prevent modal click from firing
    }
    const url = `https://www.google.com/maps/dir/?api=1&destination=${location.Latitude},${location.Longitude}`;
    window.open(url, '_blank');
  }

  ngAfterViewInit(): void {
    console.log('[Map] ngAfterViewInit called, isMobile:', this.isMobile);
    this.loadFavoriteState(); // Load favorite state from localStorage
    
    // Wait for locations to be available before initializing map
    if (!this.locations || this.locations.length === 0) {
      console.log('[Map] No locations available yet, will initialize when locations are loaded');
      return;
    }
    
    // Delay map loading to ensure DOM is ready
    // Use requestAnimationFrame to ensure layout is complete
    // For desktop, add extra delay to ensure flex layout has settled
    const delay = this.isMobile ? 0 : 100; // Extra delay for desktop
    
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Double RAF ensures layout is complete
        if (delay > 0) {
          setTimeout(() => {
            console.log('[Map] Starting checkAndLoadMap after RAF and delay');
            this.checkAndLoadMap();
          }, delay);
        } else {
          console.log('[Map] Starting checkAndLoadMap after RAF');
          this.checkAndLoadMap();
        }
      });
    });
  }

  private checkAndLoadMap(): void {
    const mapElement = document.getElementById(this.mapElementId);
    if (!mapElement) {
      console.log('[Map] Element not found, retrying...');
      // Element not found, retry after a delay (max 5 seconds)
      const retryCount = (this as any)._checkRetryCount || 0;
      if (retryCount < 25) { // 25 * 200ms = 5 seconds max
        (this as any)._checkRetryCount = retryCount + 1;
        setTimeout(() => this.checkAndLoadMap(), 200);
      } else {
        console.error('[Map] Element not found after 5 seconds');
      }
      return;
    }

    // Reset retry count
    (this as any)._checkRetryCount = 0;

    // Check if element is visible and has dimensions
    const rect = mapElement.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(mapElement);
    const isVisible = computedStyle.display !== 'none' &&
                     computedStyle.visibility !== 'hidden' &&
                     computedStyle.opacity !== '0';
    
    const hasDimensions = (mapElement.offsetWidth > 0 && mapElement.offsetHeight > 0) ||
                          (rect.width > 0 && rect.height > 0);

    console.log('[Map] Element found:', {
      offsetWidth: mapElement.offsetWidth,
      offsetHeight: mapElement.offsetHeight,
      rectWidth: rect.width,
      rectHeight: rect.height,
      display: computedStyle.display,
      visibility: computedStyle.visibility,
      opacity: computedStyle.opacity,
      isVisible,
      hasDimensions
    });

    if (!isVisible || !hasDimensions) {
      console.log('[Map] Element not ready, retrying...');
      // Element is not visible yet, wait and retry (max 5 seconds)
      const retryCount = (this as any)._visibilityRetryCount || 0;
      if (retryCount < 25) {
        (this as any)._visibilityRetryCount = retryCount + 1;
        setTimeout(() => this.checkAndLoadMap(), 200);
      } else {
        console.warn('[Map] Element still not visible after 5 seconds, proceeding anyway');
        // Proceed anyway - sometimes maps can initialize even if dimensions are 0 initially
        this.loadGoogleMaps();
      }
      return;
    }

    // Reset visibility retry count
    (this as any)._visibilityRetryCount = 0;

    console.log('[Map] Element is ready, loading Google Maps...');
    // Element is visible and has dimensions, proceed with loading
    this.loadGoogleMaps();
  }

  ngOnDestroy(): void {
    // Remove window resize listener
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = undefined;
    }
    
    // Close and remove overlay
    if (this.currentOverlay) {
      this.currentOverlay.close();
      this.currentOverlay = null;
      this.currentOverlayLocationId = null;
    }
    
    // Clear all markers
    if (this.markers) {
      this.markers.forEach(marker => {
        if (marker) {
          google.maps.event.clearInstanceListeners(marker);
          marker.setMap(null);
        }
      });
      this.markers = [];
    }
    
    // Clear map reference and event listeners
    if (this.map) {
      google.maps.event.clearInstanceListeners(this.map);
      this.map = null;
    }
    
    this.mapInitialized = false;
  }

  private loadGoogleMaps(): void {
    console.log('[Map] loadGoogleMaps called');
    
    // Check if Google Maps is already loaded
    if (typeof google !== 'undefined' && google.maps && google.maps.Map) {
      console.log('[Map] Google Maps already loaded, initializing...');
      // Small delay to ensure DOM is ready
      requestAnimationFrame(() => {
        this.initMap();
      });
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (existingScript) {
      console.log('[Map] Script already loading, waiting...');
      // Script is loading, wait for it
      const checkInterval = setInterval(() => {
        if (typeof google !== 'undefined' && google.maps && google.maps.Map) {
          console.log('[Map] Script loaded, initializing...');
          clearInterval(checkInterval);
          requestAnimationFrame(() => {
            this.initMap();
          });
        }
      }, 100);
      
      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        console.error('[Map] Google Maps API failed to load after 10 seconds');
      }, 10000);
      return;
    }

    console.log('[Map] Loading Google Maps script...');
    // Load Google Maps script with loading=async parameter to fix warning
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.GOOGLE_MAPS_API_KEY}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('[Map] Script loaded successfully, initializing...');
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        this.initMap();
      });
    };
    script.onerror = (error) => {
      console.error('[Map] Failed to load Google Maps API script:', error);
    };
    document.head.appendChild(script);
  }

  private initMap(): void {
    console.log('[Map] initMap called');
    
    if (!this.locations || this.locations.length === 0) {
      console.warn('[Map] No locations available');
      return;
    }

    // Center map on first location or selected location
    const centerLocation = this.selected || this.locations[0];
    console.log('[Map] Center location:', centerLocation);
    
    const mapOptions = {
      center: { lat: centerLocation.Latitude, lng: centerLocation.Longitude },
      zoom: 11,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    };

    const mapElement = document.getElementById(this.mapElementId);
    if (!mapElement) {
      console.error('[Map] Map element not found in initMap, ID:', this.mapElementId);
      return;
    }
    
    console.log('[Map] Map element found:', {
      offsetWidth: mapElement.offsetWidth,
      offsetHeight: mapElement.offsetHeight,
      clientWidth: mapElement.clientWidth,
      clientHeight: mapElement.clientHeight
    });

    // Ensure map element has dimensions (critical for mobile)
    // Use getBoundingClientRect for more accurate size detection
    const tryInit = () => {
      const rect = mapElement.getBoundingClientRect();
      const computedStyle = window.getComputedStyle(mapElement);
      const isVisible = computedStyle.display !== 'none' && 
                       computedStyle.visibility !== 'hidden' &&
                       computedStyle.opacity !== '0';
      
      // Check both offsetWidth/Height and getBoundingClientRect
      const hasDimensions = (mapElement.offsetWidth > 0 && mapElement.offsetHeight > 0) ||
                           (rect.width > 0 && rect.height > 0);
      
      if (!hasDimensions || !isVisible) {
        // Use IntersectionObserver to detect when element becomes visible
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && entry.intersectionRatio > 0) {
              observer.disconnect();
              // Element is now visible, try initializing
              requestAnimationFrame(() => {
                this.initializeMapInstance(mapElement, mapOptions);
              });
            }
          });
        }, {
          threshold: 0.1 // Trigger when at least 10% visible
        });
        
        observer.observe(mapElement);
        
        // Fallback timeout - if observer doesn't trigger within 5 seconds, try anyway
        setTimeout(() => {
          observer.disconnect();
          const finalRect = mapElement.getBoundingClientRect();
          const finalComputedStyle = window.getComputedStyle(mapElement);
          console.log('[Map] Fallback timeout, final check:', {
            width: finalRect.width,
            height: finalRect.height,
            offsetWidth: mapElement.offsetWidth,
            offsetHeight: mapElement.offsetHeight,
            display: finalComputedStyle.display,
            visibility: finalComputedStyle.visibility,
            parentWidth: mapElement.parentElement?.offsetWidth,
            parentHeight: mapElement.parentElement?.offsetHeight
          });
          
          // Force dimensions if parent has dimensions but element doesn't
          if (mapElement.parentElement && 
              mapElement.parentElement.offsetWidth > 0 && 
              mapElement.parentElement.offsetHeight > 0 &&
              (mapElement.offsetWidth === 0 || mapElement.offsetHeight === 0)) {
            console.log('[Map] Parent has dimensions but element does not, forcing dimensions...');
            (mapElement as HTMLElement).style.width = '100%';
            (mapElement as HTMLElement).style.height = '100%';
            (mapElement as HTMLElement).style.minHeight = '400px';
          }
          
          if (finalRect.width > 0 && finalRect.height > 0) {
            console.log('[Map] Proceeding with initialization despite timeout');
            this.initializeMapInstance(mapElement, mapOptions);
          } else {
            console.warn('[Map] Map element has no dimensions, attempting initialization anyway...');
            // Try anyway - sometimes Google Maps can handle 0 dimensions initially
            // Force a minimum size
            (mapElement as HTMLElement).style.width = mapElement.parentElement?.offsetWidth + 'px' || '100%';
            (mapElement as HTMLElement).style.height = mapElement.parentElement?.offsetHeight + 'px' || '400px';
            console.log('[Map] Attempting initialization with forced dimensions...');
            this.initializeMapInstance(mapElement, mapOptions);
          }
        }, 5000);
        
        return;
      }

      // Element has dimensions and is visible, proceed with initialization
      this.initializeMapInstance(mapElement, mapOptions);
    };
    
    // Use requestAnimationFrame to ensure layout is complete
    requestAnimationFrame(() => {
      tryInit();
    });
  }

  private initializeMapInstance(mapElement: HTMLElement, mapOptions: any): void {
    console.log('[Map] initializeMapInstance called');
    
    if (typeof google === 'undefined' || !google.maps || !google.maps.Map) {
      console.error('[Map] Google Maps API not available');
      return;
    }
    
    try {
      console.log('[Map] Creating Google Map instance...');
      this.map = new google.maps.Map(mapElement, mapOptions);
      this.mapInitialized = true;
      console.log('[Map] Map instance created successfully');
      
      // Create markers and update selected marker after map is initialized
      this.createMarkers();
      this.updateSelectedMarker();
      
      // Note: Overlay event listeners (idle, zoom_changed, drag, dragend, resize)
      // are now handled inside LocationInfoOverlay.onAdd() for better encapsulation
      
      // Trigger resize multiple times to ensure map renders correctly (especially on mobile and desktop)
      requestAnimationFrame(() => {
        if (this.map) {
          console.log('[Map] Triggering resize events...');
          google.maps.event.trigger(this.map, 'resize');
          // Additional resizes after delays
          setTimeout(() => {
            if (this.map) {
              google.maps.event.trigger(this.map, 'resize');
            }
          }, 200);
          setTimeout(() => {
            if (this.map) {
              google.maps.event.trigger(this.map, 'resize');
            }
          }, 500);
          // Extra delay for desktop layout to settle
          setTimeout(() => {
            if (this.map) {
              google.maps.event.trigger(this.map, 'resize');
            }
          }, 1000);
        }
      });
      
      // Add window resize listener for desktop
      this.resizeListener = () => {
        if (this.map) {
          google.maps.event.trigger(this.map, 'resize');
        }
      };
      window.addEventListener('resize', this.resizeListener);
    } catch (error) {
      console.error('[Map] Error initializing Google Map:', error);
    }
  }

  private createMarkers(): void {
    if (!this.map || !this.locations) return;

    // Clear existing markers
    this.markers.forEach(marker => {
      if (marker) {
        marker.setMap(null);
        google.maps.event.clearInstanceListeners(marker);
      }
    });
    this.markers = [];

    // Create new markers
    this.markers = this.locations.map(location => {
      const marker = new google.maps.Marker({
        position: { lat: location.Latitude, lng: location.Longitude },
        map: this.map,
        title: location.Name,
        animation: google.maps.Animation.DROP
      });

      marker.addListener('click', () => {
        this.select.emit(location.Id);
        this.showOverlay(marker, location);
      });

      return marker;
    });
  }

  private createOverlayContent(location: Location): HTMLElement {
    const uniqueId = `map-overlay-${this.overlayCounter++}`;
    const images = location.LocationImages?.map(img => img.ImageUrl).filter(url => url) || [];
    const hasImages = images.length > 0;
    const canNavigate = images.length > 1;
    const locationSelectUrl = this.environmentService.getFacilityConfig().locationSelectUrl || '/get-started';
    
    // Determine status based on OpeningDate
    let badgeText = '';
    if (location.OpeningDate) {
      const openingDate = new Date(location.OpeningDate);
      const now = new Date();
      
      // Coming Soon: if OpeningDate is in the future
      if (openingDate > now) {
        badgeText = 'COMING SOON';
      } 
      // New: if this is the most recently opened location (first in sorted list by date desc)
      else if (this.newestLocationId !== null && location.Id === this.newestLocationId) {
        badgeText = 'NEW';
      }
    }
    const hasBadge = !!badgeText;
    
    // Create container element
    const container = document.createElement('div');
    container.className = 'map-overlay-container';
    container.id = uniqueId;
    
    // Generate image HTML with slider wrapper for animation
    const imageHtml = hasImages 
      ? images.map((img, idx) => `
        <div id="${uniqueId}-slider-${idx}" class="map-overlay-image-slider" style="display: ${idx === 0 ? 'block' : 'none'}; width: 100%; height: 100%; position: absolute; top: 0; left: 0;">
          <img 
            id="${uniqueId}-img-${idx}"
            src="${img}"
            alt="${location.Name}"
            style="width: 100%; height: 100%; object-fit: cover; object-position: center; border-radius: 6px; display: block;"
            onerror="this.style.display='none'; document.getElementById('${uniqueId}-fallback').style.display='block';"
          />
        </div>
      `).join('')
      : '';
    
    // Generate dots HTML
    const dotsHtml = hasImages && images.length > 1
      ? images.map((_, idx) => `
        <span 
          id="${uniqueId}-dot-${idx}"
          class="map-overlay-dot"
          style="width: 4px; height: 4px; border-radius: 50%; background: ${idx === 0 ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)'}; border: 1px solid rgba(255,255,255,0.6); cursor: pointer; transition: all 0.2s ease; display: inline-block; margin: 0 2.5px;"
        ></span>
      `).join('')
      : '';

    container.innerHTML = `
      <style>
        .map-overlay-container { 
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          overflow: visible;
          position: absolute;
          z-index: 1000;
        }
        .map-overlay-container::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-top: 8px solid white;
          filter: drop-shadow(0 2px 2px rgba(0,0,0,0.1));
        }
        .map-overlay-wrapper { display: flex; flex-direction: column; gap: 0; width: ${this.isMobile ? '280px' : '360px'}; max-width: ${this.isMobile ? '85vw' : '90vw'}; }
        .map-overlay-image-section { width: 100%; height: ${this.isMobile ? '140px' : '200px'}; position: relative; }
        .map-overlay-image-container { width: 100%; height: 100%; position: relative; }
        .map-overlay-image-wrapper { width: 100%; height: 100%; overflow: hidden; background: #e9ecef; border-radius: 8px 8px 0 0; position: relative; }
        .map-overlay-image-slider { width: 100%; height: 100%; position: relative; transition: transform 0.3s ease-in-out; }
        .map-overlay-image-slider.slide-left { animation: mapOverlaySlideInLeft 0.3s ease-in-out; }
        .map-overlay-image-slider.slide-right { animation: mapOverlaySlideInRight 0.3s ease-in-out; }
        @keyframes mapOverlaySlideInLeft {
          0% { transform: translateX(-100%); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        @keyframes mapOverlaySlideInRight {
          0% { transform: translateX(100%); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        .map-overlay-badge { position: absolute; top: 10px; left: 10px; padding: 6px 10px; border-radius: 16px; font-size: 9px; font-weight: 400; letter-spacing: 0.3px; z-index: 2; background: white; color: black; border: none; text-transform: uppercase; line-height: 1; display: inline-flex; align-items: center; height: 24px; box-sizing: border-box; }
        .map-overlay-favorite { position: absolute; top: 10px; right: 38px; background: white; border: 1px solid #d0d0d0; border-radius: 50%; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 2; transition: all 0.2s ease; }
        .map-overlay-favorite:hover { background: #f8f9fa; border-color: #999; }
        .map-overlay-favorite svg { stroke: #000; fill: none; width: 12px; height: 12px; transition: all 0.2s ease; }
        .map-overlay-favorite.favorited svg { stroke: #F4364C; fill: #F4364C; }
        .map-overlay-favorite.favorited:hover svg { stroke: #F4364C; fill: #F4364C; }
        .map-overlay-close { position: absolute; top: 10px; right: 10px; background: white; border: 1px solid #d0d0d0; border-radius: 50%; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 2; transition: all 0.2s ease; }
        .map-overlay-close:hover { background: #f8f9fa; border-color: #999; }
        .map-overlay-close svg { stroke: #000; fill: none; width: 14px; height: 14px; stroke-width: 2.5; }
        .map-overlay-chevron { position: absolute; top: 50%; transform: translateY(-50%); background: rgba(255,255,255,0.85); border: none; border-radius: 50%; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 3; transition: all 0.2s ease; opacity: 0; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        .map-overlay-image-wrapper:hover .map-overlay-chevron { opacity: 0.7; }
        .map-overlay-chevron:hover { opacity: 1 !important; background: white !important; }
        .map-overlay-chevron svg { stroke: #000; fill: none; width: 12px; height: 12px; }
        .map-overlay-chevron-left { left: 10px; }
        .map-overlay-chevron-right { right: 10px; }
        .map-overlay-dots { position: absolute; bottom: 10px; left: 50%; transform: translateX(-50%); display: flex; gap: 2.5px; z-index: 2; }
        .map-overlay-content { padding: ${this.isMobile ? '8px 12px' : '12px 16px'}; display: flex; flex-direction: column; justify-content: flex-start; }
        .map-overlay-name { font-size: ${this.isMobile ? '14px' : '16px'}; font-weight: 700; color: #000; margin: 0 0 ${this.isMobile ? '2px' : '4px'} 0; line-height: 1.3; }
        .map-overlay-location { font-size: ${this.isMobile ? '11px' : '13px'}; color: #666; margin: 0 0 ${this.isMobile ? '4px' : '8px'} 0; }
        .map-overlay-rating { display: flex; align-items: center; gap: 3px; margin-bottom: ${this.isMobile ? '6px' : '12px'}; }
        .map-overlay-rating-star { color: #000; font-size: ${this.isMobile ? '12px' : '14px'}; }
        .map-overlay-rating-value { font-size: ${this.isMobile ? '12px' : '14px'}; font-weight: 700; color: #000; }
        .map-overlay-rating-count { font-size: ${this.isMobile ? '10px' : '11px'}; color: #000; }
        .map-overlay-buttons { display: flex; gap: ${this.isMobile ? '4px' : '6px'}; }
        .map-overlay-btn { display: inline-block; padding: ${this.isMobile ? '5px 12px' : '6px 16px'}; text-decoration: none; border-radius: ${this.isMobile ? '12px' : '16px'}; font-size: ${this.isMobile ? '10px' : '11px'}; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; transition: all 0.2s ease; white-space: nowrap; }
        .map-overlay-btn-select { background: #007AFF; color: white; }
        .map-overlay-btn-select:hover { background: #0056b3; }
        .map-overlay-btn-directions { background: white; color: #007AFF; border: 1px solid #007AFF; }
        .map-overlay-btn-directions:hover { background: #f8f9fa; }
      </style>
      <div class="map-overlay-wrapper">
        <!-- Top: Image Section -->
        <div class="map-overlay-image-section">
          <div class="map-overlay-image-container">
            <div class="map-overlay-image-wrapper" id="${uniqueId}-image-wrapper">
              ${hasBadge ? `<span class="map-overlay-badge">${badgeText}</span>` : ''}
              <button id="${uniqueId}-favorite" class="map-overlay-favorite ${this.favoritedLocations.has(location.Id.toString()) ? 'favorited' : ''}" type="button" aria-label="${this.favoritedLocations.has(location.Id.toString()) ? 'Remove from favorites' : 'Add to favorites'}">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
              </button>
              <button id="${uniqueId}-close" class="map-overlay-close" type="button" aria-label="Close">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
              ${imageHtml}
              <svg id="${uniqueId}-fallback" viewBox="0 0 400 300" style="display: ${hasImages ? 'none' : 'block'}; width: 100%; height: 100%; border-radius: 8px 8px 0 0;" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
                <rect width="400" height="300" fill="#e9ecef"></rect>
                <rect x="50" y="50" width="300" height="200" fill="#d0d0d0" rx="4"></rect>
                <text x="50%" y="50%" text-anchor="middle" fill="#999" font-size="14" dy=".3em">${location.Name}</text>
              </svg>
              ${canNavigate ? `
                <button id="${uniqueId}-chevron-left" class="map-overlay-chevron map-overlay-chevron-left" aria-label="Previous image">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M15 18l-6-6 6-6"/>
                  </svg>
                </button>
                <button id="${uniqueId}-chevron-right" class="map-overlay-chevron map-overlay-chevron-right" aria-label="Next image">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                </button>
              ` : ''}
              ${dotsHtml ? `<div class="map-overlay-dots">${dotsHtml}</div>` : ''}
            </div>
          </div>
        </div>
        <!-- Bottom: Content Section -->
        <div class="map-overlay-content">
          <div class="map-overlay-name">${location.Name}</div>
          <div class="map-overlay-location">${location.City}, ${location.State}</div>
          <div class="map-overlay-rating">
            <span class="map-overlay-rating-star">★</span>
            <span class="map-overlay-rating-value">${location.AverageRating || 0}</span>
            <span class="map-overlay-rating-count">(${location.ReviewCount || 0} reviews)</span>
          </div>
          <div class="map-overlay-buttons">
            <a href="${locationSelectUrl}" class="map-overlay-btn map-overlay-btn-select">SELECT</a>
            <a id="${uniqueId}-directions" href="https://www.google.com/maps/dir/?api=1&destination=${location.Latitude},${location.Longitude}" target="_blank" class="map-overlay-btn map-overlay-btn-directions">DIRECTIONS</a>
          </div>
        </div>
      </div>
    `;

    // Attach event listeners immediately after DOM is created
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      this.attachOverlayListeners(container, uniqueId, images.length, location);
    });

    return container;
  }

  private attachOverlayListeners(container: HTMLElement, uniqueId: string, totalImages: number, location: Location): void {
    // Try to find wrapper, with retry if not immediately available
    let wrapper = container.querySelector(`#${uniqueId}-image-wrapper`) as HTMLElement;
    
    // If wrapper not found, try again after a brief delay (for edge cases)
    if (!wrapper) {
      setTimeout(() => {
        wrapper = container.querySelector(`#${uniqueId}-image-wrapper`) as HTMLElement;
        if (wrapper) {
          this.setupCarouselListeners(wrapper, container, uniqueId, totalImages, location);
        }
      }, 10);
      return;
    }
    
    this.setupCarouselListeners(wrapper, container, uniqueId, totalImages, location);
  }
  
  private setupCarouselListeners(wrapper: HTMLElement, container: HTMLElement, uniqueId: string, totalImages: number, location: Location): void {
    if (!wrapper || totalImages === 0) return;

    // Carousel functions
    const carouselFunctions = {
      next: () => {
        const currentEl = container.querySelector(`#${uniqueId}-current`) as HTMLElement;
        const current = currentEl ? parseInt(currentEl.textContent || '0') : 0;
        const next = (current + 1) % totalImages;
        carouselFunctions.select(next, 'right');
      },
      prev: () => {
        const currentEl = container.querySelector(`#${uniqueId}-current`) as HTMLElement;
        const current = currentEl ? parseInt(currentEl.textContent || '0') : 0;
        const prev = (current - 1 + totalImages) % totalImages;
        carouselFunctions.select(prev, 'left');
      },
      select: (index: number, direction?: 'left' | 'right') => {
        if (index < 0 || index >= totalImages) return;
        
        const currentEl = container.querySelector(`#${uniqueId}-current`) as HTMLElement;
        const currentIndex = currentEl ? parseInt(currentEl.textContent || '0') : 0;
        
        if (!direction) {
          direction = index > currentIndex ? 'right' : 'left';
        }
        
        // Hide all images and remove animation classes
        for (let i = 0; i < totalImages; i++) {
          const slider = container.querySelector(`#${uniqueId}-slider-${i}`) as HTMLElement;
          if (slider) {
            slider.style.display = 'none';
            slider.classList.remove('slide-left', 'slide-right');
          }
          const dot = container.querySelector(`#${uniqueId}-dot-${i}`) as HTMLElement;
          if (dot) {
            dot.style.background = 'rgba(255,255,255,0.4)';
            dot.style.borderColor = 'rgba(255,255,255,0.6)';
          }
        }
        
        // Show selected image with animation
        const selectedSlider = container.querySelector(`#${uniqueId}-slider-${index}`) as HTMLElement;
        if (selectedSlider) {
          selectedSlider.style.display = 'block';
          selectedSlider.classList.add('slide-' + direction);
          setTimeout(() => {
            selectedSlider.classList.remove('slide-left', 'slide-right');
          }, 300);
        }
        
        const selectedDot = container.querySelector(`#${uniqueId}-dot-${index}`) as HTMLElement;
        if (selectedDot) {
          selectedDot.style.background = 'rgba(255,255,255,0.9)';
          selectedDot.style.borderColor = 'rgba(255,255,255,0.9)';
        }
        
        // Update current index
        let currentIndexEl = container.querySelector(`#${uniqueId}-current`) as HTMLElement;
        if (!currentIndexEl) {
          currentIndexEl = document.createElement('span');
          currentIndexEl.id = uniqueId + '-current';
          currentIndexEl.style.display = 'none';
          wrapper.appendChild(currentIndexEl);
        }
        currentIndexEl.textContent = index.toString();
      }
    };
    
    // Attach event listeners using event delegation with capture phase for immediate response
    wrapper.addEventListener('click', (e: any) => {
      const target = e.target;
      const targetId = target.id || (target.closest && target.closest('[id]') && target.closest('[id]').id);
      
      if (targetId && targetId.indexOf(uniqueId + '-chevron-left') !== -1) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        carouselFunctions.prev();
      } else if (targetId && targetId.indexOf(uniqueId + '-chevron-right') !== -1) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        carouselFunctions.next();
      } else if (targetId && targetId.indexOf(uniqueId + '-dot-') !== -1) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const dotIndex = parseInt(targetId.split('-dot-')[1]);
        if (!isNaN(dotIndex)) {
          carouselFunctions.select(dotIndex);
        }
      }
    }, true);
    
    // Also attach direct listeners to buttons as backup (for immediate response)
    const leftBtn = container.querySelector(`#${uniqueId}-chevron-left`) as HTMLElement;
    const rightBtn = container.querySelector(`#${uniqueId}-chevron-right`) as HTMLElement;
    
    if (leftBtn) {
      leftBtn.addEventListener('click', (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        carouselFunctions.prev();
      }, { capture: true });
    }
    
    if (rightBtn) {
      rightBtn.addEventListener('click', (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        carouselFunctions.next();
      }, { capture: true });
    }
    
    // Attach direct listeners to dots
    for (let i = 0; i < totalImages; i++) {
      const dot = container.querySelector(`#${uniqueId}-dot-${i}`) as HTMLElement;
      if (dot) {
        dot.addEventListener('click', (e: any) => {
          e.preventDefault();
          e.stopPropagation();
          carouselFunctions.select(i);
        }, { capture: true });
      }
    }
    
    // Attach favorite button listener
    const favoriteBtn = container.querySelector(`#${uniqueId}-favorite`) as HTMLElement;
    if (favoriteBtn) {
      favoriteBtn.addEventListener('click', (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleFavorite(location.Id.toString(), favoriteBtn);
      }, { capture: true });
    }

    // Attach close button listener
    const closeBtn = container.querySelector(`#${uniqueId}-close`) as HTMLElement;
    if (closeBtn) {
      closeBtn.addEventListener('click', (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.currentOverlay) {
          this.currentOverlay.close();
        }
      }, { capture: true });
    }
    
    // Attach SELECT button listener to store location before navigation
    const selectBtn = container.querySelector('.map-overlay-btn-select') as HTMLAnchorElement;
    if (selectBtn) {
      selectBtn.addEventListener('click', (e: any) => {
        // Store location as temp location in localStorage before navigation
        this.localStorageService.setTempLocation(location);
        // Allow navigation to proceed (don't prevent default)
      }, { capture: true });
    }
    
    // Initialize first image
    if (totalImages > 0) {
      carouselFunctions.select(0);
    }
  }

  private showOverlay(marker: any, location: Location): void {
    console.log('[Overlay] showOverlay called for location:', location.Name);
    
    // Only show map overlay on desktop - mobile uses the bottom sheet UI
    // Check both the input property and window width as a fallback
    const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
    const isActuallyMobile = this.isMobile || windowWidth <= 991;
    
    if (isActuallyMobile) {
      console.log('[Overlay] Skipping overlay on mobile - using bottom sheet instead', {
        isMobileInput: this.isMobile,
        windowWidth: windowWidth,
        isActuallyMobile: isActuallyMobile
      });
      return;
    }
    
    console.log('[Overlay] Showing overlay on desktop', {
      isMobileInput: this.isMobile,
      windowWidth: windowWidth
    });

    // Only close existing overlay if showing a different location
    // Use currentOverlayLocationId instead of selectedId since selectedId might not be updated yet
    if (this.currentOverlay && this.currentOverlayLocationId === location.Id) {
      // Same location, keep overlay open
      console.log('[Overlay] Same location, keeping overlay open', {
        currentOverlayLocationId: this.currentOverlayLocationId,
        newLocationId: location.Id
      });
      return;
    }

    // Close existing overlay only if switching to a different location
    if (this.currentOverlay) {
      console.log('[Overlay] Closing existing overlay', {
        currentOverlayLocationId: this.currentOverlayLocationId,
        newLocationId: location.Id
      });
      this.currentOverlay.close();
      this.currentOverlay = null;
      this.currentOverlayLocationId = null;
    }

    // Ensure map is ready before creating overlay
    if (!this.map) {
      console.error('[Overlay] Map not available, cannot show overlay');
      return;
    }

    // Wait for map to be fully rendered and bounds to be available
    // For location switching, we need to ensure bounds include the new location
    const ensureMapReady = (callback: () => void) => {
      try {
        const bounds = this.map.getBounds();
        const center = this.map.getCenter();
        const locationLatLng = new google.maps.LatLng(location.Latitude, location.Longitude);
        
        if (bounds && center) {
          // Check if bounds include the new location
          const containsLocation = bounds.contains(locationLatLng);
          console.log('[Overlay] Map is ready with bounds and center', {
            containsLocation,
            location: { lat: location.Latitude, lng: location.Longitude }
          });
          
          if (containsLocation) {
            // Bounds include location, proceed immediately
            callback();
          } else {
            // Bounds don't include location yet, wait for bounds to update
            console.log('[Overlay] Bounds do not include location yet, waiting for bounds_changed...');
            google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
              setTimeout(callback, 100);
            });
          }
        } else {
          console.log('[Overlay] Map bounds/center not ready yet, waiting...');
          google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
            setTimeout(callback, 100);
          });
        }
      } catch (error) {
        console.log('[Overlay] Error checking map state, proceeding anyway:', error);
        // Proceed anyway after a short delay
        setTimeout(callback, 300);
      }
    };

    ensureMapReady(() => {
      // Create content
      const content = this.createOverlayContent(location);
      const uniqueId = content.id;
      const images = location.LocationImages?.map(img => img.ImageUrl).filter(url => url) || [];
      
      // Create overlay with map reference
      const position = new google.maps.LatLng(location.Latitude, location.Longitude);
      console.log('[Overlay] Creating overlay at position:', location.Latitude, location.Longitude);
      this.currentOverlay = new LocationInfoOverlay(content, position, this.map, () => {
        console.log('[Overlay] Overlay closed');
        this.currentOverlay = null;
        this.currentOverlayLocationId = null;
      });
      
      // Track which location this overlay is showing
      this.currentOverlayLocationId = location.Id;
      
      // Set map to display overlay - this triggers onAdd() and draw()
      // Event listeners are registered in onAdd(), and scheduleDraw() handles projection readiness
      console.log('[Overlay] Setting overlay on map');
      (this.currentOverlay as any).setMap(this.map);
      
      // Attach listeners
      requestAnimationFrame(() => {
        this.attachOverlayListeners(content, uniqueId, images.length, location);
      });
    });
  }

  private updateSelectedMarker(): void {
    if (!this.map || !this.markers || !this.locations) return;

    this.markers.forEach((marker, index) => {
      const location = this.locations![index];
      if (location.Id === this.selectedId) {
        marker.setAnimation(google.maps.Animation.BOUNCE);
        setTimeout(() => marker.setAnimation(null), 2000);
        
        // Center map on selected location
        this.map.setCenter({ lat: location.Latitude, lng: location.Longitude });
        this.map.setZoom(15);
        
        // Wait for map to finish centering/zooming before showing overlay
        // Check if map is already at the target position (might not trigger idle if already there)
        const targetCenter = new google.maps.LatLng(location.Latitude, location.Longitude);
        const currentCenter = this.map.getCenter();
        const currentZoom = this.map.getZoom();
        const isAlreadyAtPosition = currentCenter && 
                                    currentZoom === 15 &&
                                    Math.abs(currentCenter.lat() - location.Latitude) < 0.001 &&
                                    Math.abs(currentCenter.lng() - location.Longitude) < 0.001;
        
        if (isAlreadyAtPosition) {
          // Map is already at target position, show overlay immediately after a brief delay
          console.log('[Map] Map already at target position, showing overlay');
          setTimeout(() => {
            this.showOverlay(marker, location);
          }, 300);
        } else {
          // Map needs to move, wait for idle event
          console.log('[Map] Map needs to move, waiting for idle event');
          google.maps.event.addListenerOnce(this.map, 'idle', () => {
            console.log('[Map] Map is idle, showing overlay (overlay will retry until projection is ready)');
            
            // Give the map a moment to fully settle after idle
            setTimeout(() => {
              this.showOverlay(marker, location);
            }, 300);
          });
        }
      }
    });
  }

  onPick(id: number): void {
    this.select.emit(id);
    setTimeout(() => {
      this.updateSelectedMarker();
      this.cdr.markForCheck();
    }, 100);
  }

  private toggleFavorite(locationId: string, buttonElement: HTMLElement): void {
    if (this.favoritedLocations.has(locationId)) {
      this.favoritedLocations.delete(locationId);
      buttonElement.classList.remove('favorited');
      buttonElement.setAttribute('aria-label', 'Add to favorites');
    } else {
      this.favoritedLocations.add(locationId);
      buttonElement.classList.add('favorited');
      buttonElement.setAttribute('aria-label', 'Remove from favorites');
    }
    
    // Persist to localStorage as fallback
    this.saveFavoriteState();
    
    // TODO(INTEG): Persist favorite state to backend/API (replace localStorage)
  }

  private loadFavoriteState(): void {
    try {
      const stored = localStorage.getItem('favoriteLocations');
      if (stored) {
        this.favoritedLocations = new Set(JSON.parse(stored));
      }
    } catch (error) {
      console.warn('Failed to load favorite state from localStorage', error);
    }
  }

  private saveFavoriteState(): void {
    try {
      localStorage.setItem('favoriteLocations', JSON.stringify(Array.from(this.favoritedLocations)));
    } catch (error) {
      console.warn('Failed to save favorite state to localStorage', error);
    }
  }
}
